import { useQuery } from '@tanstack/react-query';
import { getClassGroups } from '@/services/class-groups';
import { EntityImage } from '@/lib/entity-image';
import type { SubstituteInstructor } from './class-availabilities';

interface Slot {
  slot_id: string | null;
  override_slot_id: string | null;
  weekly_availability_id: string;
  instructor_schedule_id: string;
  class_id: string;
  admin_user_id: string;
  start_time: number;
  end_time: number;
  participants_limit: number | null;
  total_spots: number | null;
  is_fully_booked?: boolean;
  waitlist_enabled?: boolean;
  waitlist_count?: number;
  created_at: string;
  updated_at: string;
}

interface WeeklyAvailability {
  weekly_availability_id: string;
  instructor_schedule_id: string;
  class_id: string;
  admin_user_id: string;
  weekday:
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY'
    | 'SUNDAY';
  status: 'ACTIVE' | 'INACTIVE';
  created_at: string;
  updated_at: string;
  slots: Slot[];
}

interface InstructorSchedule {
  instructor_schedule_id: string;
  class_id: string;
  admin_user_id: string;
  timezone: string;
  use_fixed_duration_in_minutes: boolean;
  fixed_duration_in_minutes: number;
  use_fixed_participants_limit: boolean;
  fixed_participants_limit: number;
  created_at: string;
  updated_at: string;
  admin_user?: {
    first_name: string;
    last_name: string | null;
    admin_user_id: string;
    profile_picture: string | null;
  };
  is_substituted?: boolean;
  original_instructor?: SubstituteInstructor | null;
  weekly_availability: WeeklyAvailability[];
  // The detail read only: the schedule's rules resolved as checkout resolves
  // them, null where no level sets one.
  booking_rules?: {
    min_notice_hours: number | null;
    max_days_in_advance: number | null;
    modification_notice_hours: number | null;
  };
}

export interface Class {
  class_id: string;
  class_group_id: string;
  admin_user_id: string;
  tenant_id: string;
  order: number;
  status: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  name: string;
  short_description: string;
  description: string | null;
  image: string | null;
  images?: EntityImage[];
  slug: string;
  availability: 'PUBLIC' | 'PRIVATE' | 'UNAVAILABLE';
  // A ONE_TIME class runs on a single date.
  kind?: 'REGULAR' | 'ONE_TIME';
  // UTC ISO start and end of a ONE_TIME class's date, on the class page and the
  // class list. The availability grid can say which slots are bookable but not
  // when the class runs: it hides an occurrence until booking opens and drops
  // it once it starts. The page and the list card read the day from the start
  // in the visitor's zone, which is the zone the grid counts days in.
  next_occurrence_start?: string | null;
  next_occurrence_end?: string | null;
  // Class page only: whether the class list shows this class's times. An
  // older backend leaves it out.
  has_listed_times?: boolean;
  // On the class page and the class list: who teaches that date, a cover's
  // substitute before the schedule's owner, as the grid names them.
  next_occurrence_instructor?:
    | (NonNullable<InstructorSchedule['admin_user']> & {
        is_substituted: boolean;
      })
    | null;
  price: number | null;
  location: string | null;
  currency: string;
  use_min_notice_hours: boolean;
  min_notice_hours: number;
  use_max_days_in_advance: boolean;
  max_days_in_advance: number;
  use_modification_notice_hours: boolean;
  modification_notice_hours: number;
  use_min_spots_per_booking: boolean;
  min_spots_per_booking: number | null;
  confirmation_content: string | null;
  created_at: string;
  updated_at: string;
  instructor_schedule_id?: string;
  instructor_schedules?: InstructorSchedule[];
}

export type ClassGroup = {
  class_group_id: string;
  admin_user_id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  slug: string;
  // Not sent by the public list, which returns ACTIVE groups only.
  status?: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  order: number;
  classes: Class[];
};

export default function useClassGroups({
  include_classes = false,
  enabled = true,
}: {
  include_classes: boolean;
  enabled?: boolean;
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['classGroups', include_classes],
    queryFn: () => getClassGroups({ include_classes }),
    enabled,
    // A failed list has to stay failed. The home page and the pack and
    // membership pages withhold the views that observe this same query while
    // it loads, so on the default `retryOnMount` each of them mounting
    // refetched it, flipped the page back to loading and unmounted the view
    // again: a request every round trip, the skeleton never settling.
    retryOnMount: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
